import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import is from '@sindresorhus/is';
import type { Property } from 'csstype';
import type { FunctionComponent, MouseEventHandler, PropsWithChildren } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';

import { AlignmentContextProvider } from '../alignment/AlignmentContext';
import { useAlignmentStore } from '../alignment/use-alignment-store';
import { AlertCircleIcon } from './icons/AlertCircleIcon';
import { LayoutThemeProvider } from './LayoutThemeProvider';

const SquareBlock = styled.div<{ readonly color: 'primary' | 'secondary' }>(({ color }) => ({
    backgroundColor: color === 'primary' ? '#040F16' : '#C7C7C5',
    height: '0.5rem',
    width: '0.5rem',
}));

const SquareBlocksWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    justifyContent: 'space-between',
    width: 'fit-content',
});

const SquareBlocksStack = styled.div({
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
});

type SquareBlocksProperties = {
    readonly amountOfBottomBlocks: number;
    readonly amountOfTopBlocks: number;
};

const SquareBlocks: FunctionComponent<SquareBlocksProperties> = ({ amountOfBottomBlocks, amountOfTopBlocks }) => {
    return (
        <SquareBlocksWrapper>
            <SquareBlocksStack>
                {Array.from({ length: amountOfTopBlocks }).map((_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                    <SquareBlock key={index} color="primary" />
                ))}
            </SquareBlocksStack>
            <SquareBlocksStack>
                {Array.from({ length: amountOfBottomBlocks }).map((_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                    <SquareBlock key={index} color="secondary" />
                ))}
            </SquareBlocksStack>
        </SquareBlocksWrapper>
    );
};

const SquaresSectionInnerWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
});

const SquaresSection: FunctionComponent = () => {
    return (
        <SquaresSectionInnerWrapper>
            <SquareBlocks amountOfBottomBlocks={3} amountOfTopBlocks={2} />
            <SquareBlocks amountOfBottomBlocks={1} amountOfTopBlocks={1} />
            <SquareBlocks amountOfBottomBlocks={18} amountOfTopBlocks={10} />
        </SquaresSectionInnerWrapper>
    );
};

const StyledText = styled.p({
    color: '#53565B',
    fontSize: '0.875rem',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '0.875rem',
});

const StyledNumber = styled.div<{ readonly isNumberHighlighted?: boolean }>(({ isNumberHighlighted = false }) => ({
    color: isNumberHighlighted ? '#FC531F' : '#040F16',
    fontSize: '1.25rem',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '1.875rem',
}));

const HorizontalDivider = styled.div({
    backgroundColor: '#E7E7E7',
    height: 'auto',
    width: '1px',
});

const VerticalDivider = styled.div({
    backgroundColor: '#E7E7E7',
    height: '1px',
    width: 'auto',
});

type HomeStatisticItemProperties = {
    readonly isNumberHighlighted?: boolean;
    readonly number: number;
    readonly text: string;
};

const HomeStatisticItem: FunctionComponent<HomeStatisticItemProperties> = ({
    isNumberHighlighted = false,
    number,
    text,
}) => {
    return (
        <Box>
            <StyledNumber isNumberHighlighted={isNumberHighlighted}>{number}</StyledNumber>
            <StyledText>{text}</StyledText>
        </Box>
    );
};

const HomeStatistics: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);
    const notAlignedCount = useAlignmentStore((state) => state.notAlignedCount);
    const uncertainCount = useAlignmentStore((state) => state.uncertainCount);
    const alignedCount = useAlignmentStore((state) => state.alignedCount);
    const hireCount = useAlignmentStore((state) => state.hireCount);
    const teamSizeCount = useAlignmentStore((state) => state.teamSizeCount);

    const statistics = [
        {
            number: notAlignedCount,
            text: translate('pages.home.not-aligned'),
        },
        {
            number: uncertainCount,
            text: translate('pages.home.uncertain'),
        },
        {
            number: alignedCount,
            text: translate('pages.home.aligned'),
        },
        {
            number: hireCount,
            text: translate('pages.home.hire'),
        },
        {
            number: teamSizeCount,
            text: translate('pages.home.team-size'),
        },
    ];

    return (
        <Stack direction="row" gap="2rem">
            {statistics.map((statistic) => (
                <HomeStatisticItem key={statistic.text} number={statistic.number} text={statistic.text} />
            ))}
        </Stack>
    );
};

const WelcomeSection: FunctionComponent = () => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { myUserProfile } = applicationState;

    return (
        <Box>
            <Typography variant="h4">{`${translate('common.welcome')}, ${myUserProfile.get('firstName').unwrapOr('')}`}</Typography>
            <Typography color="#53565B" variant="body1" width="37.5rem">
                {translate('pages.home.welcome-message')}
            </Typography>
        </Box>
    );
};

const HomeCardStack = styled.div<{ readonly cursor: Property.Cursor; readonly opacity: number }>(
    ({ cursor, opacity }) => ({
        border: '1px solid #DADAD8',
        borderRadius: '0.75rem',
        cursor,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        opacity,
        padding: '0.75rem',
        width: '17rem',
    }),
);

const HomeCardHeadline = styled.p({
    color: '#040F16',
    fontSize: '0.875rem',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '1.25rem',
    margin: 0,
});

const HomeCardSubHeadline = styled.p<{ readonly subHeadlineColor: string }>(({ subHeadlineColor }) => ({
    color: subHeadlineColor,
    fontSize: '0.75rem',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '1rem',
    margin: 0,
}));

const HomeCardButton = styled.button({
    background: 'none',
    border: 'none',
    color: '#53565B',
    cursor: 'inherit',
    fontSize: '0.875rem',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '1.25rem',
    marginTop: '1rem',
    padding: '0',
    width: 'fit-content',
});

type HomeCardProperties = {
    readonly buttonText: string;
    readonly disabled?: boolean;
    readonly headlineText: string;
    readonly isSubHeadlineHighlighted?: boolean;
    readonly onClick?: MouseEventHandler;
    readonly subHeadlineText: string;
};

const HomeCardSubHeadlineWitchIconWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
});

const HomeCard: FunctionComponent<HomeCardProperties> = ({
    buttonText,
    disabled = false,
    headlineText,
    isSubHeadlineHighlighted = false,
    onClick,
    subHeadlineText,
}) => {
    const subHeadlineColor = disabled ? '#6E6E6C' : isSubHeadlineHighlighted ? '#D92D20' : '#6E6E6C';
    const opacity = disabled ? 0.6 : 1;

    return (
        <HomeCardStack cursor={is.function(onClick) ? 'pointer' : 'not-allowed'} opacity={opacity} onClick={onClick}>
            <Box>
                <HomeCardHeadline>{headlineText}</HomeCardHeadline>
                <HomeCardSubHeadlineWitchIconWrapper>
                    <HomeCardSubHeadline subHeadlineColor={subHeadlineColor}>{subHeadlineText}</HomeCardSubHeadline>
                    <div>
                        <AlertCircleIcon isHighlighted={isSubHeadlineHighlighted} />
                    </div>
                </HomeCardSubHeadlineWitchIconWrapper>
            </Box>
            <HomeCardButton>{buttonText}</HomeCardButton>
        </HomeCardStack>
    );
};

const HomeCards: FunctionComponent = () => {
    const { actions, translate } = useContext(ApplicationContext);

    return (
        <Stack direction="row" spacing={2}>
            <HomeCard
                buttonText={translate('pages.home.get-started')}
                headlineText={translate('pages.home.define-company-structure')}
                isSubHeadlineHighlighted={true}
                subHeadlineText={translate('pages.home.required-to-invite-team-members')}
                onClick={() => actions.navigateTo('/job-and-skill-architecture/details')}
            />
            <HomeCard
                buttonText={translate('pages.home.coming-soon')}
                disabled={true}
                headlineText={translate('pages.home.define-purpose')}
                subHeadlineText={translate('pages.home.required-to-invite-team-members')}
            />
            <HomeCard
                buttonText={translate('pages.home.coming-soon')}
                disabled={true}
                headlineText={translate('pages.home.invite-team-members')}
                subHeadlineText={translate('pages.home.required-to-see-company-alignment')}
            />
        </Stack>
    );
};

const containerMarginTop = '2rem';
const containerMarginBottom = '2rem';

const Container = styled.div({
    margin: `${containerMarginTop} 1.5rem ${containerMarginBottom} 9rem`,
});

const InnerWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - ${containerMarginTop} - ${containerMarginBottom} - 64px)`,
    justifyContent: 'space-between',
});

const SampleItem = styled.div({
    backgroundColor: '#F1F1F0',
    borderRadius: '0.25rem',
    color: '#6E6E6C',
    display: 'inline-block',
    fontSize: '0.625rem',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '0.750rem',
    marginTop: '1rem',
    padding: '0.25rem 0.5rem',
    textTransform: 'uppercase',
});

const InviteTeamButton = styled.div({
    background: '#E7E7E7',
    borderRadius: '0.625rem',
    color: '#FBFBFF',
    cursor: 'pointer',
    display: 'inline-block',
    fontSize: '0.875rem',
    fontStyle: 'normal',
    fontWeight: 600,
    height: 'fit-content',
    lineHeight: '1.25rem',
    padding: '0.5rem 0.75rem',
});

const InnerWrapperWithEvenPaddings: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return <InnerWrapper>{children}</InnerWrapper>;
};

const TrialModalWrapper = styled.div({
    backdropFilter: 'blur(10px)',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    bottom: 0,
    left: 0,
    opacity: 1,
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 1101,
});

const TrialModal = styled.div({
    backgroundColor: '#F6F6F7',
    borderRadius: '1rem',
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '37.5rem',
    zIndex: 3,
});

const ModalHeader = styled.p({
    color: '#040F16',
    fontSize: '1.5rem',
    fontWeight: 500,
    lineHeight: '2.25rem',
    margin: '0 0 0.25rem',
});

const ModalDescription = styled.p({
    fontSize: '1.25rem',
    fontWeight: 400,
    lineHeight: '1.875rem',
    margin: 0,
});

const ModalContentSectionWrapper = styled.div({
    padding: '1rem',
});

const ModalContentButtonsContainer = styled.div({
    display: 'flex',
    gap: '1rem',
    justifyContent: 'end',
});

const StyledButton = styled.button<{ readonly variant: 'primary' | 'secondary' }>(({ variant }) => ({
    backgroundColor: variant === 'primary' ? '#040F16' : '#FBFBFF',
    border: variant === 'primary' ? 'none' : '1px solid #353E45',
    borderRadius: '0.625rem',
    color: variant === 'primary' ? '#FBFBFF' : '#353E45',
    cursor: 'pointer',
    fontSize: '1.25rem',
    fontWeight: 600,
    height: '2.5rem',
    padding: '0.5rem 0.75rem',
    textDecoration: 'none',
}));

const ModalContent: FunctionComponent = () => {
    const {
        actions,
        applicationState: { myUserProfile },
        translate,
    } = useContext(ApplicationContext);
    const contactSalesViaEmail = (): void => {
        const username = `${myUserProfile.get('firstName').unwrapOr('')} ${myUserProfile.get('lastName').unwrapOr('')}`;

        const body = `${translate('trial.sales-email.body')} ${username}`;
        const subject = translate('trial.sales-email.subject');

        actions.mailTo({
            body,
            email: 'sales@cobrainer.com',
            subject,
        });
    };

    const contactHanns = (): void => {
        actions.openWindow('https://www.cobrainer.com/');
    };

    return (
        <div>
            <ModalContentSectionWrapper>
                <ModalHeader>{translate('trial.trial-ended')}</ModalHeader>
                <ModalDescription>{translate('trial.thank-you')}</ModalDescription>
            </ModalContentSectionWrapper>
            <VerticalDivider />
            <ModalContentSectionWrapper>
                <ModalContentButtonsContainer>
                    <StyledButton variant="secondary" onClick={contactSalesViaEmail}>
                        {translate('trial.contact-sales')}
                    </StyledButton>
                    <StyledButton variant="primary" onClick={contactHanns}>
                        {translate('trial.talk-to-hanns')}
                    </StyledButton>
                </ModalContentButtonsContainer>
            </ModalContentSectionWrapper>
        </div>
    );
};

const AlignmentScore: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);
    const alignmentScore = useAlignmentStore((state) => state.alignmentScore);

    return (
        <HomeStatisticItem
            isNumberHighlighted={true}
            number={alignmentScore}
            text={translate('pages.home.alignment')}
        />
    );
};

export const Home: FunctionComponent = () => {
    const {
        applicationState: { customization },
        translate,
    } = useContext(ApplicationContext);

    return (
        <AlignmentContextProvider>
            <LayoutThemeProvider>
                {customization.get('tenantMode').mapOr(null, (mode) =>
                    mode === 'deactivated' ? (
                        <TrialModalWrapper>
                            <TrialModal>
                                <ModalContent />
                            </TrialModal>
                        </TrialModalWrapper>
                    ) : null,
                )}
                <Container>
                    <InnerWrapperWithEvenPaddings>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                                <Stack direction="row" gap="2rem">
                                    <AlignmentScore />
                                    <HorizontalDivider />
                                    <HomeStatistics />
                                </Stack>
                                <SampleItem>{translate('trial.sample')}</SampleItem>
                            </div>
                            <InviteTeamButton>{translate('trial.invite-team')}</InviteTeamButton>
                        </div>
                        <SquaresSection />
                        <Stack gap="4rem">
                            <WelcomeSection />
                            <HomeCards />
                        </Stack>
                    </InnerWrapperWithEvenPaddings>
                </Container>
            </LayoutThemeProvider>
        </AlignmentContextProvider>
    );
};

export const Component = Home;
Component.displayName = 'Home';
